/**
 * AdminHeroPinPage — /admin/hero-pin (V9.13 Sprint 2 UX)
 *
 * Gestion de l'épinglage HERO Athlète (admin/COPF).
 *  - Voir le pin actif (avec athlète, dates, motif, auteur).
 *  - Créer un pin (search athlète + dates début/fin obligatoires + motif optionnel).
 *  - Prolonger le pin actif.
 *  - Retirer le pin actif (unpin).
 *  - Voir aperçu du classement automatique (top 10).
 *
 * Toutes les actions sont auditées (backend).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Star, Loader2, ShieldAlert, ArrowLeft, Search, Trophy, X, Clock, User, Award,
  RefreshCw, PlusCircle, CalendarClock,
} from "lucide-react";

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(s) {
  if (!s) return null;
  const d = new Date(s);
  return d.toISOString();
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminHeroPinPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isModerator = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [activePin, setActivePin] = useState(null);
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isModerator) return;
    setLoading(true);
    try {
      const [pinsRes, prevRes] = await Promise.all([
        api.get("/admin/hero/pins"),
        api.get("/admin/hero/preview", { params: { limit: 10 } }).catch(() => ({ data: { items: [] } })),
      ]);
      const now = new Date();
      const active = (pinsRes.data.items || []).find((p) => new Date(p.start_at) <= now && new Date(p.end_at) >= now);
      setActivePin(active || null);
      setPreview(prevRes.data.items || []);
    } catch (e) { showError(e, { prefix: "HERO :" }); }
    finally { setLoading(false); }
  }, [isModerator, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const doUnpin = async () => {
    try {
      await api.post("/admin/hero/unpin", null, { params: { reason: "manual_unpin_admin_ui" } });
      toast.success("Pin retiré, scoring automatique en cours.");
      refresh();
    } catch (e) { showError(e, { prefix: "Retirer :" }); }
  };

  if (!isModerator) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="hero-pin-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">La gestion du HERO est réservée aux admins et à la COPF.</p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/")}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />Retour à l'accueil
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-5" data-testid="admin-hero-pin-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Star className="h-6 w-6 text-[hsl(var(--aito-gold))]" />HERO Athlète à la une
          </h1>
          <p className="text-sm text-muted-foreground">
            Épingle temporellement un athlète en tête de la page d'accueil. À expiration, le scoring automatique reprend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="hero-pin-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
          </Button>
          <Button size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" onClick={() => setPinDialogOpen(true)} data-testid="hero-pin-create">
            <PlusCircle className="h-4 w-4 mr-1.5" />Épingler un athlète
          </Button>
        </div>
      </div>

      {/* Pin actif */}
      <Card className="p-4" data-testid="hero-pin-active-card">
        <div className="flex items-center gap-2 mb-3">
          <Star className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
          <h2 className="font-display font-semibold text-base">Pin actif</h2>
        </div>
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : !activePin ? (
          <div className="text-sm text-muted-foreground">Aucun pin actif. Le HERO utilise le classement automatique.</div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-[hsl(var(--aito-gold))] text-black border-0" data-testid="hero-pin-active-name">
                  {activePin.athlete_full_name || activePin.athlete_id}
                </Badge>
                {activePin.athlete_slug && (
                  <a href={`/athletes/${activePin.athlete_slug}`} target="_blank" rel="noreferrer" className="text-xs text-[hsl(var(--aito-lagoon))] hover:underline">
                    Voir la fiche
                  </a>
                )}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
                <CalendarClock className="h-3 w-3" />
                <span>Du {formatDate(activePin.start_at)} au {formatDate(activePin.end_at)}</span>
              </div>
              {activePin.reason && (
                <div className="text-xs text-muted-foreground mt-1"><span className="font-medium">Motif :</span> {activePin.reason}</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setPinDialogOpen(true)} data-testid="hero-pin-extend">
                <Clock className="h-3.5 w-3.5 mr-1" />Remplacer / Prolonger
              </Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={doUnpin} data-testid="hero-pin-unpin">
                <X className="h-3.5 w-3.5 mr-1" />Retirer
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Aperçu classement auto */}
      <Card className="p-4" data-testid="hero-pin-preview-card">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <h2 className="font-display font-semibold text-base">Aperçu du classement automatique</h2>
          <span className="text-xs text-muted-foreground">(déterministe, sans pin)</span>
        </div>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : preview.length === 0 ? (
          <div className="text-sm text-muted-foreground">Aucun athlète éligible actuellement.</div>
        ) : (
          <ul className="space-y-2">
            {preview.map((p, idx) => (
              <li key={p.id} className="flex items-center justify-between gap-3 p-2 rounded-md border" data-testid={`hero-preview-row-${idx}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[10px] font-mono">#{idx + 1}</Badge>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.full_name || p.id}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.main_discipline && <span className="capitalize">{p.main_discipline.replace(/-/g, " ")}</span>}
                      {p.island && <span> · {p.island}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <Badge className="bg-[hsl(var(--aito-lagoon))]/10 text-[hsl(var(--aito-lagoon))] border-0" data-testid={`hero-preview-score-${idx}`}>
                    <Award className="h-3 w-3 mr-1" />{p.total}
                  </Badge>
                  <span className="hidden sm:inline text-[10px] text-muted-foreground">
                    perf {p.components?.performance} · act {p.components?.activity}
                    {p.components?.cooldown < 0 ? ` · cool ${p.components.cooldown}` : ""}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Dialog création/remplacement */}
      {pinDialogOpen && (
        <PinDialog
          open={pinDialogOpen}
          onOpenChange={(v) => { if (!v) setPinDialogOpen(false); }}
          existingPin={activePin}
          onDone={() => { setPinDialogOpen(false); refresh(); }}
        />
      )}
    </div>
  );
}

// ---------- Dialog ----------
function PinDialog({ open, onOpenChange, existingPin, onDone }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState(existingPin?.reason || "");

  // Dates par défaut : maintenant → +7j
  const now = new Date();
  const in7 = new Date(now.getTime() + 7 * 24 * 3600 * 1000);
  const [startAt, setStartAt] = useState(existingPin ? toDatetimeLocalValue(existingPin.start_at) : toDatetimeLocalValue(now));
  const [endAt, setEndAt] = useState(existingPin ? toDatetimeLocalValue(existingPin.end_at) : toDatetimeLocalValue(in7));

  // Recherche athlète (debounced)
  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/entity-links/targets", { params: { kind: "athlete", q: query, limit: 8 } });
        if (!cancelled) setResults(data?.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally { if (!cancelled) setSearching(false); }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!selectedAthlete) return false;
    if (!startAt || !endAt) return false;
    if (new Date(endAt) <= new Date(startAt)) return false;
    if (new Date(endAt) <= new Date()) return false;
    return true;
  }, [busy, selectedAthlete, startAt, endAt]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/hero/pin", {
        athlete_id: selectedAthlete.id,
        start_at: fromDatetimeLocalValue(startAt),
        end_at: fromDatetimeLocalValue(endAt),
        reason: reason.trim() || null,
      });
      toast.success("Athlète épinglé.");
      onDone?.();
    } catch (e) { showError(e, { prefix: "Épingler :" }); }
    finally { setBusy(false); }
  };

  const suggestDays = (days) => {
    const start = new Date();
    const end = new Date(start.getTime() + days * 24 * 3600 * 1000);
    setStartAt(toDatetimeLocalValue(start));
    setEndAt(toDatetimeLocalValue(end));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="hero-pin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-[hsl(var(--aito-gold))]" />
            {existingPin ? "Remplacer le pin actif" : "Épingler un athlète"}
          </DialogTitle>
          <DialogDescription>
            {existingPin
              ? "Le pin actuel sera remplacé et audité."
              : "Sélectionne un athlète et définit la durée d'épinglage. À expiration, le scoring automatique reprend."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Recherche athlète */}
          <div>
            <Label className="text-xs">Athlète <span className="text-destructive">*</span></Label>
            {selectedAthlete ? (
              <div className="mt-1 flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30">
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{selectedAthlete.full_name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {selectedAthlete.discipline || selectedAthlete.main_discipline || "—"}
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setSelectedAthlete(null)} data-testid="hero-pin-clear-athlete">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher un athlète par nom…"
                    className="pl-8"
                    data-testid="hero-pin-search-athlete"
                  />
                </div>
                {searching && <div className="text-xs text-muted-foreground mt-1">Recherche…</div>}
                {!searching && results.length > 0 && (
                  <ul className="mt-1 border rounded-md divide-y max-h-48 overflow-y-auto">
                    {results.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          className="w-full text-left p-2 text-sm hover:bg-muted"
                          onClick={() => { setSelectedAthlete(r); setResults([]); setQuery(""); }}
                          data-testid={`hero-pin-athlete-option-${r.id}`}
                        >
                          <span className="font-medium">{r.full_name || r.name}</span>
                          {(r.discipline || r.main_discipline) && (
                            <span className="text-[11px] text-muted-foreground"> · {r.discipline || r.main_discipline}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          {/* Suggestions durée */}
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[11px] text-muted-foreground mr-1">Durée rapide :</span>
            {[3, 7, 14, 30].map((d) => (
              <Button key={d} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => suggestDays(d)} data-testid={`hero-pin-quick-${d}d`}>
                {d}j
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Début <span className="text-destructive">*</span></Label>
              <Input type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="hero-pin-start-at" />
            </div>
            <div>
              <Label className="text-xs">Fin <span className="text-destructive">*</span></Label>
              <Input type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="hero-pin-end-at" />
            </div>
          </div>
          {new Date(endAt) <= new Date(startAt) && (
            <div className="text-[11px] text-destructive">La date de fin doit être postérieure au début.</div>
          )}

          <div>
            <Label className="text-xs">Motif (optionnel, tracé dans l'audit)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Ex : Sacré champion du monde…" data-testid="hero-pin-reason" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="hero-pin-cancel">Annuler</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="hero-pin-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Star className="h-4 w-4 mr-1.5" />}
            Épingler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
